import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { ReactNode, Suspense, lazy, useEffect, useState } from "react";
import { FiLink2 } from "react-icons/fi";
import { Link, type To } from "react-router";
import { isDesktopDevice } from "~/utils/device";
import { track, type AppDownloadDirection } from "~/utils/analytics";

const QRCode = lazy(() => import("react-qr-code"));

export type CTADialogTracking = {
  direction: AppDownloadDirection;
  surface: string;
  appsflyerType?: string;
};

type CTADialogProps = {
  open: boolean;
  onClose: () => void;
  link: string;
  title: string;
  description: ReactNode;
  qrTitle?: string;
  qrDescription?: ReactNode;
  iconUrl?: string;
  buttonLabel?: string;
  showCopyLink?: boolean;
  forceLayout?: "qr" | "button";
  buttonTo?: To;
  buttonDisabled?: boolean;
  tracking?: CTADialogTracking;
};

const DEFAULT_QR_DESCRIPTION = "Scan to join, or copy the link to your phone.";

export function CTADialog({
  open,
  onClose,
  link,
  title,
  description,
  qrTitle,
  qrDescription,
  iconUrl,
  buttonLabel = "Get the app",
  showCopyLink = true,
  forceLayout,
  buttonTo,
  buttonDisabled = false,
  tracking,
}: CTADialogProps) {
  const layout = useResolvedLayout(forceLayout);

  const fireTracking = () => {
    if (!tracking) return;
    track("app_download_clicked", {
      direction: tracking.direction,
      surface: tracking.surface,
      appsflyer_type: tracking.appsflyerType,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      transition
      className="fixed inset-0 z-50 flex w-screen items-center justify-center p-4 transition duration-300 ease-out data-closed:opacity-0"
    >
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            className={`relative w-full ${layout === "qr" ? "max-w-[223px]" : "max-w-[330px]"} rounded-[26px] bg-butter-cream-light p-[30px] backdrop-blur-[8.5px] shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:data-closed:translate-y-0 sm:data-closed:scale-95`}
          >
            {layout === "qr" ? (
              <QrLayout
                link={link}
                title={qrTitle ?? title}
                description={qrDescription ?? DEFAULT_QR_DESCRIPTION}
                showCopyLink={showCopyLink}
                onCopyLink={fireTracking}
              />
            ) : (
              <ButtonLayout
                link={link}
                title={title}
                description={description}
                iconUrl={iconUrl}
                buttonLabel={buttonLabel}
                buttonTo={buttonTo}
                buttonDisabled={buttonDisabled}
                onButtonClick={() => {
                  fireTracking();
                  if (buttonTo) onClose();
                }}
              />
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

function useResolvedLayout(forceLayout?: "qr" | "button"): "qr" | "button" {
  const [layout, setLayout] = useState<"qr" | "button">(forceLayout ?? "qr");

  useEffect(() => {
    if (forceLayout) {
      setLayout(forceLayout);
      return;
    }
    setLayout(isDesktopDevice() ? "qr" : "button");
  }, [forceLayout]);

  return layout;
}

type QrLayoutProps = {
  link: string;
  title: string;
  description?: ReactNode;
  showCopyLink: boolean;
  onCopyLink?: () => void;
};

function QrLayout({
  link,
  title,
  description,
  showCopyLink,
  onCopyLink,
}: QrLayoutProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      onCopyLink?.();
    } catch {
      // ignore — clipboard API may not be available
    }
  };

  return (
    <div className="flex flex-col items-center gap-[18px]">
      <div className="rounded-[6px] bg-white p-2">
        <Suspense fallback={<div className="h-[148px] w-[147px]" />}>
          <QRCode value={link} size={132} fgColor="#4A5043" />
        </Suspense>
      </div>
      <div className="flex flex-col items-center gap-3 text-center">
        <DialogTitle
          as="p"
          className="font-sans text-base font-bold leading-tight text-butter-green"
        >
          {title}
        </DialogTitle>
        {description && (
          <div className="font-sans text-base leading-[1.4] text-butter-green">
            {description}
          </div>
        )}
      </div>
      {showCopyLink && (
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-butter-green px-3 py-2 font-sans text-xs text-butter-green transition-opacity hover:opacity-80"
        >
          <FiLink2 size={12} aria-hidden />
          {copied ? "Link copied" : "Copy link"}
        </button>
      )}
    </div>
  );
}

type ButtonLayoutProps = {
  link: string;
  title: string;
  description: ReactNode;
  iconUrl?: string;
  buttonLabel: string;
  buttonTo?: To;
  buttonDisabled?: boolean;
  onButtonClick?: () => void;
};

const BUTTON_CLASSES =
  "flex w-full items-center justify-center rounded-xl bg-butter-pink px-7 py-[18px] font-sans text-base font-bold text-butter-cream-light transition-opacity hover:opacity-90";
const DISABLED_BUTTON_CLASSES = `${BUTTON_CLASSES} cursor-default opacity-50 hover:opacity-50`;

function ButtonLayout({
  link,
  title,
  description,
  iconUrl,
  buttonLabel,
  buttonTo,
  buttonDisabled = false,
  onButtonClick,
}: ButtonLayoutProps) {
  return (
    <div className="flex flex-col items-center gap-[18px]">
      {iconUrl && (
        <img src={iconUrl} alt="" aria-hidden className="h-[60px] w-auto" />
      )}
      <div className="flex flex-col items-center gap-3 text-center">
        <DialogTitle
          as="p"
          className="font-serif text-[22.5px] leading-tight text-butter-green"
        >
          {title}
        </DialogTitle>
        <div className="font-sans text-base leading-tight text-butter-green tracking-[-0.16px]">
          {description}
        </div>
      </div>
      {buttonDisabled ? (
        <button type="button" disabled className={DISABLED_BUTTON_CLASSES}>
          {buttonLabel}
        </button>
      ) : buttonTo ? (
        <Link to={buttonTo} onClick={onButtonClick} className={BUTTON_CLASSES}>
          {buttonLabel}
        </Link>
      ) : (
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          onClick={onButtonClick}
          className={BUTTON_CLASSES}
        >
          {buttonLabel}
        </a>
      )}
    </div>
  );
}
